import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Loader2, ExternalLink, Check, X, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { UnverifiedNormsSection } from "../components/admin/UnverifiedNormsSection";

const ENTITY_TYPE_OPTIONS = [
  { v: "nacional", l: "Nacional" },
  { v: "territorial_municipal", l: "Territorial municipal" },
  { v: "territorial_departamental", l: "Territorial departamental" },
  { v: "descentralizada", l: "Descentralizada" },
  { v: "empresa_industrial_comercial_estado", l: "Empresa industrial/comercial del Estado" },
  { v: "otra", l: "Otra" },
];

/**
 * RF-04 — Admin review de normas propuestas por usuarios.
 * Lista `estado_revision = pendiente`, permite validar o rechazar con motivo.
 */
export default function AdminNormativaReviewPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actioningId, setActioningId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null); // item being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [validateOpen, setValidateOpen] = useState(null); // item being validated
  const [applicableTypes, setApplicableTypes] = useState([]);
  const [categoria, setCategoria] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await api.get("/admin/normativa/pending");
      setItems(resp.items || []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openValidate = (it) => {
    setApplicableTypes(it.aplicable_entity_types || []);
    setCategoria(it.categoria === "user_submission" ? "" : it.categoria || "");
    setValidateOpen(it);
  };

  const toggleType = (t) => {
    setApplicableTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  };

  const submitValidate = async () => {
    if (!validateOpen) return;
    setActioningId(validateOpen.id);
    try {
      await api.post(`/admin/normativa/${validateOpen.id}/validate`, {
        aplicable_entity_types: applicableTypes,
        categoria: categoria.trim() || undefined,
      });
      toast.success("Norma validada");
      setValidateOpen(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setActioningId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectOpen) return;
    if (rejectReason.trim().length < 10) {
      toast.error("Motivo mínimo 10 caracteres");
      return;
    }
    setActioningId(rejectOpen.id);
    try {
      await api.post(`/admin/normativa/${rejectOpen.id}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success("Norma rechazada");
      setRejectOpen(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="admin-normativa-review-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Revisión de normativa propuesta</h1>
          <p className="text-sm text-gray-500 mt-1">
            Normas propuestas por usuarios pendientes de validación. Al validar se incluyen en la lista aplicable.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="btn-refresh"
          className="rounded-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded-sm" data-testid="error-banner">
          {error}
        </div>
      )}

      <UnverifiedNormsSection />

      {loading && items.length === 0 ? (
        <div className="text-center py-10 text-gray-500" data-testid="loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Cargando...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-500 border border-dashed border-gray-200 rounded-sm" data-testid="empty-state">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          No hay normas pendientes de revisión.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="normativa-list">
          {items.map((it) => (
            <li
              key={it.id}
              className="border border-gray-200 bg-white p-5 rounded-sm space-y-3"
              data-testid={`norm-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">{it.tipo}</Badge>
                    <Badge variant="outline" className="font-mono">
                      {it.numero} / {it.anio}
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                      Pendiente
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-lg mt-2">{it.alias}</h3>
                  <p className="text-sm text-gray-500">
                    Emisora: <span className="text-gray-700">{it.entidad_emisora}</span>
                  </p>
                </div>
                {it.url_oficial && (
                  <a
                    href={it.url_oficial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary flex items-center gap-1 hover:underline"
                    data-testid={`norm-url-${it.id}`}
                  >
                    Fuente oficial <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              <div className="text-sm text-gray-700">
                <strong className="text-gray-900">Aplicabilidad:</strong>{" "}
                <span className="whitespace-pre-wrap">{it.aplicabilidad}</span>
              </div>

              {it.aplicable_entity_types?.length > 0 && (
                <div className="text-xs text-gray-500">
                  <strong>Aplica a:</strong>{" "}
                  {it.aplicable_entity_types.join(", ")}
                </div>
              )}

              <div className="text-xs text-gray-400">
                Propuesta por {it.submitted_by_email || it.submitted_by} • {new Date(it.created_at).toLocaleString("es-CO")}
              </div>

              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <Button
                  size="sm"
                  onClick={() => openValidate(it)}
                  disabled={actioningId === it.id}
                  data-testid={`validate-${it.id}`}
                  className="rounded-sm"
                >
                  <Check className="w-4 h-4 mr-1.5" />
                  Validar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRejectOpen(it)}
                  disabled={actioningId === it.id}
                  data-testid={`reject-${it.id}`}
                  className="rounded-sm text-red-700 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Rechazar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Validate dialog */}
      <Dialog open={!!validateOpen} onOpenChange={(o) => !o && setValidateOpen(null)}>
        <DialogContent data-testid="validate-dialog">
          <DialogHeader>
            <DialogTitle>Validar norma</DialogTitle>
            <DialogDescription>
              Confirma aplicabilidad y categoría. Al validar entra en la lista pública de normas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="text-sm">
              <strong>{validateOpen?.tipo} {validateOpen?.numero}/{validateOpen?.anio}</strong>
              <p className="text-gray-500">{validateOpen?.alias}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Aplicable a tipos de entidad</Label>
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPE_OPTIONS.map((o) => (
                  <button
                    type="button"
                    key={o.v}
                    onClick={() => toggleType(o.v)}
                    className={`text-xs px-3 py-1.5 border rounded-sm ${
                      applicableTypes.includes(o.v)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-gray-300 text-gray-700 hover:border-gray-400"
                    }`}
                    data-testid={`type-${o.v}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">Sin selección = aplicable a todas.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="val-cat">Categoría (opcional)</Label>
              <Input
                id="val-cat"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                placeholder="Ej. Procedimiento, Anticorrupción..."
                data-testid="validate-cat-input"
                className="rounded-sm h-10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateOpen(null)} className="rounded-sm">
              Cancelar
            </Button>
            <Button
              onClick={submitValidate}
              disabled={actioningId === validateOpen?.id}
              data-testid="validate-confirm"
              className="rounded-sm"
            >
              {actioningId === validateOpen?.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Validar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={(o) => !o && setRejectOpen(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar norma</DialogTitle>
            <DialogDescription>
              Explica brevemente al usuario por qué se rechaza (mínimo 10 caracteres).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            placeholder="Ej. La norma propuesta no está vigente / no corresponde al ámbito contractual..."
            data-testid="reject-reason-input"
            className="rounded-sm"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)} className="rounded-sm">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={actioningId === rejectOpen?.id || rejectReason.trim().length < 10}
              data-testid="reject-confirm"
              className="rounded-sm"
            >
              {actioningId === rejectOpen?.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
